import type { GridScope } from './types'

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export type StorageReadResult =
  | { status: 'missing' }
  | { status: 'value'; value: string }
  | { status: 'unreadable' }

export const readStorageItemState = (key: string): StorageReadResult => {
  const storage = getStorage()
  if (!storage) return { status: 'unreadable' }
  try {
    const value = storage.getItem(key)
    return value === null ? { status: 'missing' } : { status: 'value', value }
  } catch {
    return { status: 'unreadable' }
  }
}

export const readStorageItem = (key: string): string | null => {
  const result = readStorageItemState(key)
  return result.status === 'value' ? result.value : null
}

export const listStorageKeys = (prefix?: string): string[] => {
  const storage = getStorage()
  if (!storage) return []

  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (!key) continue
      if (prefix && !key.startsWith(prefix)) continue
      keys.push(key)
    }
    return keys
  } catch {
    return []
  }
}

export const writeStorageItem = (key: string, value: string): boolean => {
  const storage = getStorage()
  if (!storage) return false
  try {
    storage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

export const removeStorageItem = (key: string): boolean => {
  const storage = getStorage()
  if (!storage) return false
  try {
    storage.removeItem(key)
    return true
  } catch {
    return false
  }
}

export const buildScopeKey = (scope: GridScope) =>
  [scope.view, scope.orgId, scope.teamId, scope.projectId]
    .filter(Boolean)
    .join('-')

export const buildStorageKey = (
  prefix: string,
  scopeKey: string,
  suffix?: string,
) => [prefix, scopeKey, suffix].filter(Boolean).join('-')
